import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Api } from '../services/api/axios-config';
import { setCsrfRequestToken } from '../services/api/axios-config/interceptors';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

export type PermissionState = 'prompt' | 'granted' | 'denied' | 'unsupported';

export interface PushNotificationContextValue {
  /** Estado atual da permissão de notificações push */
  permissionState: PermissionState;
  /** Se o navegador / dispositivo suporta push notifications */
  pushSupported: boolean;
  /** Se já existe uma subscription ativa */
  isSubscribed: boolean;
  /** Se é necessário um gesto do usuário (clique) para ativar (iOS PWA) */
  needsUserGesture: boolean;
  /** Chama pushManager.subscribe() dentro de um click handler (user gesture) */
  subscribeWithGesture: () => Promise<void>;
  /** Tentar novamente o fluxo de inscrição */
  retry: () => void;
}

const PushNotificationContext = createContext<PushNotificationContextValue>({
  permissionState: 'unsupported',
  pushSupported: false,
  isSubscribed: false,
  needsUserGesture: false,
  subscribeWithGesture: async () => {
    throw new Error('PushNotificationProvider não encontrado no componente.');
  },
  retry: () => {
    throw new Error('PushNotificationProvider não encontrado no componente.');
  },
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const VAPID_PLACEHOLDER = 'VAPID_PUBLIC_KEY';

const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface Props {
  children: React.ReactNode;
  /** Informa se o usuário está autenticado — dispara subscribe/unsubscribe */
  isAuthenticated: boolean;
}

export const PushNotificationProvider: React.FC<Props> = ({ children, isAuthenticated }) => {
  const [permissionState, setPermissionState] = useState<PermissionState>(
    'serviceWorker' in navigator && 'PushManager' in window
      ? 'prompt'
      : 'unsupported'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [needsUserGesture, setNeedsUserGesture] = useState(false);

  const subscribedRef = useRef(false);
  const vapidKeyRef = useRef<string | null>(null);

  const pushSupported =
    'serviceWorker' in navigator &&
    'PushManager' in window;

  // -----------------------------------------------------------------------
  // VAPID key (cacheada)
  // -----------------------------------------------------------------------
  const getVapidPublicKey = useCallback(async (): Promise<string | null> => {
    if (vapidKeyRef.current) return vapidKeyRef.current;

    try {
      const { data } = await Api.get<{ publicKey: string }>('/push/vapid-public-key');
      if (data?.publicKey && data.publicKey !== VAPID_PLACEHOLDER) {
        vapidKeyRef.current = data.publicKey;
        return data.publicKey;
      }
    } catch {
      // fallback
    }

    const envKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    if (envKey && envKey !== VAPID_PLACEHOLDER) {
      vapidKeyRef.current = envKey;
      return envKey;
    }
    return null;
  }, []);

  // -----------------------------------------------------------------------
  // Upsert subscription no backend
  // -----------------------------------------------------------------------
  const upsertSubscription = useCallback(async (subscription: PushSubscription) => {
    // Refresh CSRF token before subscribing — token may be stale after login
    try {
      const tokenRes = await Api.get('/auth/antiforgery-token');
      if (tokenRes.data) setCsrfRequestToken(tokenRes.data as string);
    } catch {
      // best-effort
    }
    const subJson = subscription.toJSON();
    await Api.post('/push/subscribe', {
      endpoint: subJson.endpoint,
      p256dh: subJson.keys?.p256dh,
      auth: subJson.keys?.auth,
    });
  }, []);

  // -----------------------------------------------------------------------
  // Fluxo principal de inscrição
  // -----------------------------------------------------------------------
  const doSubscribe = useCallback(async (fromUserGesture: boolean) => {
    if (subscribedRef.current && !fromUserGesture) return;

    try {
      const registration = await navigator.serviceWorker.ready;

      // iOS não-PWA: pushManager é undefined
      if (!registration.pushManager) {
        setPermissionState('unsupported');
        return;
      }

      const vapidKey = await getVapidPublicKey();
      if (!vapidKey) {
        console.log('[Push] VAPID key não configurada.');
        return;
      }
      const convertedKey = urlBase64ToUint8Array(vapidKey);

      // (a) Checa permissão via PushManager (funciona em todas as plataformas)
      const state = await registration.pushManager.permissionState({ userVisibleOnly: true });
      setPermissionState(state);

      if (state === 'denied') {
        console.log('[Push] Permissão negada.');
        return;
      }

      // (b) Já inscrito? Upsert no backend (estado só depois do upsert)
      const existing = await registration.pushManager.getSubscription();
      if (existing) {
        await upsertSubscription(existing);
        setIsSubscribed(true);
        setNeedsUserGesture(false);
        subscribedRef.current = true;
        return;
      }

      // (c) Tenta inscrever — pushManager.subscribe() mostra o prompt nativo
      try {
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: convertedKey as any,
        });

        await upsertSubscription(subscription);
        setIsSubscribed(true);
        setNeedsUserGesture(false);
        subscribedRef.current = true;
        console.log('[Push] Inscrição realizada com sucesso.');
      } catch (err: any) {
        // NotAllowedError no auto-subscribe (sem gesture) = iOS PWA precisa de clique
        // NotAllowedError com gesture = usuário negou no prompt nativo
        if (!fromUserGesture) {
          setNeedsUserGesture(true);
          console.log('[Push] iOS: precisa de interação do usuário para ativar push.');
        } else {
          setPermissionState('denied');
          console.log('[Push] Permissão negada pelo usuário.');
        }
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[Push] Erro no fluxo de subscription:', err);
      }
    }
  }, [getVapidPublicKey, upsertSubscription]);

  // -----------------------------------------------------------------------
  // Chamado pelo clique no botão do banner (TEM user gesture)
  // -----------------------------------------------------------------------
  const subscribeWithGesture = useCallback(async () => {
    setNeedsUserGesture(false);
    await doSubscribe(true);
  }, [doSubscribe]);

  // -----------------------------------------------------------------------
  // Retry
  // -----------------------------------------------------------------------
  const retry = useCallback(() => {
    setNeedsUserGesture(false);
    setPermissionState('prompt');
    subscribedRef.current = false;
    setTimeout(() => doSubscribe(false), 500);
  }, [doSubscribe]);

  // -----------------------------------------------------------------------
  // Unsubscribe
  // -----------------------------------------------------------------------
  const unsubscribe = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager?.getSubscription();
      if (subscription) {
        const subJson = subscription.toJSON();
        await subscription.unsubscribe();
        if (subJson.endpoint) {
          await Api.post('/push/unsubscribe', { endpoint: subJson.endpoint });
        }
      }
    } catch {
      // silencioso no logout
    }
    setIsSubscribed(false);
    subscribedRef.current = false;
  }, []);

  // -----------------------------------------------------------------------
  // Effect: monitora auth
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!pushSupported) return;

    if (isAuthenticated) {
      // Pequeno delay para garantir SW ativo
      const timer = setTimeout(() => doSubscribe(false), 1000);
      return () => clearTimeout(timer);
    } else if (subscribedRef.current) {
      // Só faz unsubscribe se estava inscrito (evita chamada desnecessária no mount)
      unsubscribe();
    }
  }, [isAuthenticated, pushSupported, doSubscribe, unsubscribe]);

  return (
    <PushNotificationContext.Provider
      value={{
        permissionState,
        pushSupported,
        isSubscribed,
        needsUserGesture,
        subscribeWithGesture,
        retry,
      }}
    >
      {children}
    </PushNotificationContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook de consumo
// ---------------------------------------------------------------------------
export const usePushNotificationContext = () => useContext(PushNotificationContext);
